import React, { useState } from "react";
import { FaSearch, FaEdit } from "react-icons/fa";
import { BsChevronDown } from "react-icons/bs";
import Footer from "../../components/footer";
import FooterButton from "../../components/footerButton";
import { toast } from "react-toastify";
import SelectContributorsModal from "../../components/Admin/SelectContributorsModal";
import AssignChecklistModal from "../../components/Admin/AssignChecklistModal";
import { GetClinicalSitesByOrg } from "../../hooks/getClinicalSitesByOrg";
import usePatientsByClinicalSite from "../../hooks/usePatientsByClinicalSite";
import useAdmitPatient from "../../hooks/useAdmitPatient";

const ManageAdmissions = () => {
  const [selectedClinicalSite, setSelectedClinicalSite] = useState("");
  const [selectedClinicalSiteId, setSelectedClinicalSiteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [clinicalSiteDropdownOpen, setClinicalSiteDropdownOpen] =
    useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showRestOfForm, setShowRestOfForm] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [admissionDropdownOpen, setAdmissionDropdownOpen] = useState(false);

  // Fetch clinical sites data
  const {
    data: clinicalSitesData,
    isLoading: clinicalSitesLoading,
    error: clinicalSitesError,
  } = GetClinicalSitesByOrg();

  // Fetch patients by clinical site
  const {
    data: patientsData,
    isLoading: patientsLoading,
    error: patientsError,
  } = usePatientsByClinicalSite(selectedClinicalSiteId);

  // Admit patient mutation
  const {
    mutate: admitPatientMutate,
    isLoading: isAdmitting,
    error: admissionError,
    isSuccess: admissionSuccess,
  } = useAdmitPatient();

  // Form state
  const [formData, setFormData] = useState({
    healthcareNumber: "",
    expiryDate: "",
    admissionDate: "",
    clinicalSite: "",
    location: "",
    functionalChecklist: "",
  });

  // Modal data state
  const [selectedContributors, setSelectedContributors] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);

  // Get clinical sites from hook data
  const clinicalSites = clinicalSitesData?.data || [];

  // Get patients from hook data
  const patients = patientsData?.data?.content || [];

  const locations = [
    "Emergency Department",
    "ICU",
    "Medical Ward",
    "Surgical Ward",
    "Pediatrics",
  ];

  const admissionSites = [
    "Emergency Department",
    "Direct Admission",
    "Transfer from Other Facility",
    "Outpatient Clinic",
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Filter patients based on search term
  const getFilteredPatients = () => {
    if (!searchTerm.trim() || !selectedClinicalSiteId || !patients.length)
      return [];

    // Client-side filtering through all patients
    return patients
      .filter(
        (patient) =>
          `${patient.firstName} ${patient.lastName}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (patient.pommeId &&
            String(patient.pommeId)
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (patient.ohip &&
            String(patient.ohip)
              .toLowerCase()
              .includes(searchTerm.toLowerCase())),
      )
      .slice(0, 5); // Limit to 5 results
  };

  const handleSearchChange = (value) => {
    if (!selectedClinicalSiteId || patientsLoading) {
      return; // Don't allow search if no clinical site is selected or still loading
    }
    setSearchTerm(value);
    setSearchDropdownOpen(value.trim().length > 0);
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.firstName} ${patient.lastName}`);
    setSearchDropdownOpen(false);
    setShowRestOfForm(true);

    // Auto-fill the healthcare number when patient is selected
    handleInputChange("healthcareNumber", patient.ohip);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchDropdownOpen(false);
    setSelectedPatient(null);
    setShowRestOfForm(false);
    // Clear healthcare number when search is cleared
    handleInputChange("healthcareNumber", "");
  };

  const handleClear = () => {
    setFormData({
      healthcareNumber: "",
      expiryDate: "",
      admissionDate: "",
      clinicalSite: "",
      location: "",
      functionalChecklist: "",
    });
    setSearchTerm("");
    setSelectedPatient(null);
    setShowRestOfForm(false);
    setSelectedClinicalSite("");
    setSelectedClinicalSiteId(null);
    setSearchDropdownOpen(false);
    setSelectedContributors([]);
    setSelectedChecklist(null);
  };

  // Transform contributors to careProviders format. The modal already holds the
  // privileges as CareProviderPrivilege values, so they go through as-is.
  const transformContributorsToCareProviders = (contributors) => {
    return contributors.map((contributor) => ({
      careProviderId: contributor.id,
      privileges: contributor.privileges || [],
    }));
  };

  const handleSaveForLater = () => {
    if (!selectedPatient) {
      toast.error("Please select a patient first");
      return;
    }

    const submissionData = {
      careProviders: transformContributorsToCareProviders(selectedContributors),
      checklistId: selectedChecklist?.id || "",
    };
    admitPatientMutate(
      {
        patientId: selectedPatient.pommeId,
        admissionData: submissionData,
      },
      {
        onSuccess: (data) => {
          toast.success("Patient admission saved for later successfully!");
          // Optionally redirect or clear form
        },
        onError: (error) => {
          toast.error("Failed to save admission. Please try again.");
          console.error("Save for later error:", error);
        },
      },
    );
  };

  const handleSaveAndSign = () => {
    if (!selectedPatient) {
      toast.error("Please select a patient first");
      return;
    }

    const submissionData = {
      careProviders: transformContributorsToCareProviders(selectedContributors),
      checklistId: selectedChecklist?.id || "",
    };

    admitPatientMutate(
      {
        patientId: selectedPatient.pommeId,
        admissionData: submissionData,
      },
      {
        onSuccess: (data) => {
          toast.success("Patient admission completed and signed successfully!");
          // Optionally redirect or clear form
        },
        onError: (error) => {
          toast.error("Failed to complete admission. Please try again.");
          console.error("Save and sign error:", error);
        },
      },
    );
  };

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body mb-3">
            <h5 className="font-weight-lighter mb-4">
              Admin: Manage Admissions
            </h5>
            <hr />

            <div className="noScroll h-90 verticalScroll pb-17">
              {/* Clinical Site Dropdown */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <div className="dropdown">
                    <button
                      className="btn btn-block border text-left overlay-dropdown-input small-text"
                      type="button"
                      onClick={() =>
                        setClinicalSiteDropdownOpen(!clinicalSiteDropdownOpen)
                      }
                      disabled={clinicalSitesLoading}
                    >
                      {clinicalSitesLoading
                        ? "Loading clinical sites..."
                        : selectedClinicalSite || "Choose Clinical Site"}
                      <BsChevronDown
                        size={12}
                        style={{
                          transform: clinicalSiteDropdownOpen
                            ? "rotate(180deg)"
                            : "rotate(0)",
                          transition: "transform 0.3s ease",
                          float: "right",
                          marginTop: "3px",
                        }}
                      />
                    </button>
                    <div
                      className={`dropdown-menu w-100 ${
                        clinicalSiteDropdownOpen ? "show" : ""
                      }`}
                    >
                      <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                        Choose one site only:
                      </p>
                      {clinicalSitesLoading ? (
                        <div className="dropdown-item small-text text-muted">
                          Loading clinical sites...
                        </div>
                      ) : clinicalSitesError ? (
                        <div className="dropdown-item small-text text-danger">
                          Error loading clinical sites
                        </div>
                      ) : clinicalSites.length > 0 ? (
                        clinicalSites.map((site) => (
                          <a
                            key={site.id}
                            className="dropdown-item small-text"
                            href="#"
                            onClick={() => {
                              setSelectedClinicalSite(site.name);
                              setSelectedClinicalSiteId(site.id);
                              setClinicalSiteDropdownOpen(false);
                              // Reset search and form when clinical site changes
                              setSearchTerm("");
                              setSelectedPatient(null);
                              setShowRestOfForm(false);
                              setSearchDropdownOpen(false);
                              // Clear form data and modal selections
                              setFormData({
                                healthcareNumber: "",
                                expiryDate: "",
                                admissionDate: "",
                                clinicalSite: "",
                                location: "",
                                functionalChecklist: "",
                              });
                              setSelectedContributors([]);
                              setSelectedChecklist(null);
                            }}
                          >
                            {site.name}
                          </a>
                        ))
                      ) : (
                        <div className="dropdown-item small-text text-muted">
                          No clinical sites available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient Search */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <div className="dropdown">
                    <div className="position-relative">
                      <FaSearch
                        size={12}
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#6c757d",
                          zIndex: 2,
                        }}
                      />
                      <input
                        type="text"
                        className="btn btn-block border text-left overlay-dropdown-input small-text"
                        placeholder={
                          !selectedClinicalSiteId
                            ? "Select clinical site first..."
                            : patientsLoading
                              ? "Loading patients..."
                              : "Search patients..."
                        }
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        disabled={!selectedClinicalSiteId || patientsLoading}
                        style={{
                          paddingLeft: "35px",
                          paddingRight: searchTerm ? "35px" : "12px",
                          textAlign: "left",
                          opacity:
                            selectedClinicalSiteId && !patientsLoading
                              ? 1
                              : 0.6,
                        }}
                      />
                      {/* {searchTerm && (
                    //   <FaTimes
                    //     size={12}
                    //     style={{
                    //       position: "absolute",
                    //       right: "16px",
                    //       top: "50%",
                    //       transform: "translateY(-50%)",
                    //       color: "#6c757d",
                    //       zIndex: 3,
                    //       cursor: "pointer",
                    //     }}
                    //     onClick={clearSearch}
                    //   />
                    )} */}
                    </div>

                    {/* Search Results Dropdown */}
                    <div
                      className={`dropdown-menu w-100 ${
                        searchDropdownOpen ? "show" : ""
                      }`}
                    >
                      <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                        Search Results:
                      </p>
                      {patientsLoading ? (
                        <div className="dropdown-item small-text text-muted">
                          Loading patients...
                        </div>
                      ) : patientsError ? (
                        <div className="dropdown-item small-text text-danger">
                          Error loading patients
                        </div>
                      ) : !patients.length ? (
                        <div className="dropdown-item small-text text-muted">
                          No patients in this clinical site
                        </div>
                      ) : getFilteredPatients().length > 0 ? (
                        getFilteredPatients().map((patient) => (
                          <a
                            key={patient.id}
                            className="dropdown-item small-text"
                            href="#"
                            onClick={() => handlePatientSelect(patient)}
                          >
                            <div className="d-flex justify-content-between">
                              <span>
                                {patient.firstName} {patient.lastName}
                              </span>
                              <small className="text-muted">
                                {patient.pommeId || patient.id}
                              </small>
                            </div>
                            <small className="text-muted">{patient.ohip}</small>
                          </a>
                        ))
                      ) : searchTerm.trim() ? (
                        <div className="dropdown-item small-text text-muted">
                          No patients found matching "{searchTerm}"
                        </div>
                      ) : (
                        <div className="dropdown-item small-text text-muted">
                          Type to search through {patients.length} patients
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Fields - Only show after patient selection */}
              {showRestOfForm && (
                <div className="row">
                  {/* Left Column */}
                  <div className="col-md-6 col-12">
                    {/* Healthcare Number */}
                    <div className="row form-group mb-4">
                      <label
                        className="col-md-4 form-label text-muted mb-2 fs-13"
                        style={{ fontWeight: "500" }}
                      >
                        Healthcare Number:
                      </label>
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="small-text overlay-input"
                          value={formData.healthcareNumber}
                          onChange={(e) =>
                            handleInputChange(
                              "healthcareNumber",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>

                    {/* Expiry Date */}
                    <div className="form-group row mb-4">
                      <label
                        className="col-md-4 form-label text-muted mb-2 fs-13"
                        style={{ fontWeight: "500" }}
                      >
                        Expiry Date:
                      </label>
                      <div className="col-md-8">
                        <input
                          type="date"
                          className="small-text overlay-input"
                          value={formData.expiryDate}
                          onChange={(e) =>
                            handleInputChange("expiryDate", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* Admission Date */}
                    <div className="form-group row mb-4">
                      <label
                        className="col-md-4 form-label text-muted mb-2 fs-13"
                        style={{ fontWeight: "500" }}
                      >
                        Admission Date:
                      </label>
                      <div className="col-md-8">
                        <input
                          type="date"
                          className="small-text overlay-input"
                          value={formData.admissionDate}
                          onChange={(e) =>
                            handleInputChange("admissionDate", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* Clinical Site */}
                    <div className="form-group row mb-4">
                      <label
                        className="col-md-4 form-label text-muted mb-2 fs-13"
                        style={{ fontWeight: "500" }}
                      >
                        Clinical site:
                      </label>
                      <div className="col-md-8">
                        <div className="dropdown">
                          <button
                            className="btn btn-block border text-left d-flex justify-content-between align-items-center w-205 h-35"
                            type="button"
                            style={{
                              backgroundColor: "#fff",
                              borderColor: "#ced4da",
                              borderRadius: "6px",
                              padding: "12px 16px",
                              fontSize: "14px",
                              color: formData.clinicalSite
                                ? "#495057"
                                : "#6c757d",
                            }}
                            onClick={() =>
                              setAdmissionDropdownOpen(!admissionDropdownOpen)
                            }
                          >
                            <span className="small-text">
                              {formData.clinicalSite || "Choose Clinical Site"}
                            </span>
                            <BsChevronDown
                              style={{
                                transform: admissionDropdownOpen
                                  ? "rotate(180deg)"
                                  : "rotate(0)",
                                transition: "all 0.3s ease",
                              }}
                            />
                          </button>
                          {admissionDropdownOpen && (
                            <div
                              className="dropdown-menu show border-0 shadow mt-1"
                              style={{ borderRadius: "6px" }}
                            >
                              {admissionSites.map((site, index) => (
                                <a
                                  key={index}
                                  className="dropdown-item small-text"
                                  href="#"
                                  onClick={() => {
                                    handleInputChange("clinicalSite", site);
                                    setAdmissionDropdownOpen(false);
                                  }}
                                >
                                  {site}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="form-group row mb-4">
                      <label
                        className="col-md-4 form-label text-muted mb-2 fs-13"
                        style={{ fontWeight: "500" }}
                      >
                        Location:
                      </label>
                      <div className="col-md-8">
                        <div className="dropdown">
                          <button
                            className="btn btn-block border text-left d-flex justify-content-between align-items-center w-205 h-35"
                            type="button"
                            style={{
                              backgroundColor: "#fff",
                              borderColor: "#ced4da",
                              borderRadius: "6px",
                              padding: "12px 16px",
                              fontSize: "14px",
                              color: formData.location ? "#495057" : "#6c757d",
                            }}
                            onClick={() =>
                              setLocationDropdownOpen(!locationDropdownOpen)
                            }
                          >
                            <span className="small-text">
                              {formData.location || "Choose Location"}
                            </span>
                            <BsChevronDown
                              style={{
                                transform: locationDropdownOpen
                                  ? "rotate(180deg)"
                                  : "rotate(0)",
                                transition: "all 0.3s ease",
                              }}
                            />
                          </button>
                          {locationDropdownOpen && (
                            <div
                              className="dropdown-menu show w-100 border-0 shadow mt-1"
                              style={{ borderRadius: "6px" }}
                            >
                              {locations.map((location, index) => (
                                <a
                                  key={index}
                                  className="dropdown-item small-text"
                                  href="#"
                                  onClick={() => {
                                    handleInputChange("location", location);
                                    setLocationDropdownOpen(false);
                                  }}
                                >
                                  {location}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="col-md-6 col-12">
                    {/* Contributors */}
                    <div className="row mb-2">
                      <label
                        className="col-md-4 text-muted fs-13"
                        style={{ fontWeight: "500" }}
                      >
                        Contributors:
                      </label>
                      <div className="col-md-8">
                        <button
                          className="btn btn-outline-primary fs-13 w-205 "
                          data-toggle="modal"
                          data-target="#selectContributors"
                        >
                          {selectedContributors.length > 0
                            ? `${selectedContributors.length} Contributors Selected`
                            : "Select Contributors"}
                        </button>
                        {/* Select Contributors Modal */}
                        <SelectContributorsModal
                          selectedContributors={selectedContributors}
                          onContributorsChange={setSelectedContributors}
                        />
                        {/* Assign Checklist Modal */}
                        <AssignChecklistModal
                          selectedChecklist={selectedChecklist}
                          onChecklistChange={setSelectedChecklist}
                          allowMultiple={false}
                        />
                      </div>
                    </div>

                    {/* Functional Checklist */}
                    <div className="row">
                      <label
                        className="col-md-4 text-muted fs-13"
                        style={{ fontWeight: "500" }}
                      >
                        Functional Checklist:
                      </label>
                      <div className="col-md-8">
                        <button
                          className="btn btn-outline-primary fs-13 w-205"
                          data-toggle="modal"
                          data-target="#assignChecklist"
                        >
                          {selectedChecklist
                            ? `Checklist: ${selectedChecklist.name}`
                            : "Assign Checklist"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showRestOfForm ? (
        <FooterButton
          child1={
            <button
              className="btn btn_danger px-5 mt-3 mt-sm-0"
              onClick={handleClear}
              disabled={isAdmitting}
            >
              <small>CLEAR</small>
            </button>
          }
          child2={
            <div className="flex-grow-1 flex-grow-sm-0 d-flex justify-content-between justify-content-sm-end">
              <button
                className="btn btn_outline_light px-3 mx-0 mx-md-3"
                onClick={handleSaveForLater}
                disabled={isAdmitting}
              >
                <small>{isAdmitting ? "SAVING..." : "SAVE FOR LATER"}</small>
              </button>
              <button
                className="btn btn_light pl-4 pr-2 ml-3"
                onClick={handleSaveAndSign}
                disabled={isAdmitting}
              >
                <small className="mx-3">
                  {isAdmitting ? "SIGNING..." : "SAVE & SIGN"}
                </small>
              </button>
            </div>
          }
        />
      ) : (
        <Footer />
      )}
    </>
  );
};

export default ManageAdmissions;
